import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../../auth/store/authStore'
import { propertiesService } from '../../../services/propertiesService'
import { peopleService } from '../../../services/peopleService'
import { teamsService } from '../../../services/teamsService'
import { useSelectedPropertyType } from '../../../store/propertyStore'
import type { AdminTab } from '../constants/adminConstants'
import type { Property, User, Team, QRCodeConfigResponse } from '../../../types/api.types'
import { AdminTabBar } from './AdminTabBar'
import { UsersTab } from './UsersTab'
import { PropertiesTab } from './PropertiesTab'
import { DevicesTab } from './DevicesTab'
import { TeamsTab } from './TeamsTab'
import { TablesTab } from './TablesTab'
import { PrintersTab } from './PrintersTab'
import { QrCodesTab } from './QrCodesTab'
import { QuickCommSetupModal } from './QuickCommSetupModal'

export function AdminSettingsPage(): JSX.Element {
  const user = useAuthStore((s) => s.user)
  const propertyType = useSelectedPropertyType()
  const isRestaurant = propertyType === 'RESTAURANT'
  const [activeTab, setActiveTab] = useState<AdminTab>('users')

  // Shared data for QuickComm modal lookups
  const [properties, setProperties] = useState<Property[]>([])
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [allTeams, setAllTeams] = useState<Team[]>([])

  // QuickComm modal bridge
  const [showQRModal, setShowQRModal] = useState(false)
  const [qrCodeData, setQrCodeData] = useState<QRCodeConfigResponse | null>(null)

  // Load shared reference data on mount
  useEffect(() => {
    async function loadSharedData() {
      try {
        const [props, users, teams] = await Promise.all([
          propertiesService.getAll(),
          peopleService.getAll(),
          teamsService.getAll(),
        ])
        setProperties(props)
        setAllUsers(users)
        setAllTeams(teams)
      } catch {
        // Individual tabs handle their own errors
      }
    }
    loadSharedData()
  }, [])

  function handleOpenQuickCommSetup(data: QRCodeConfigResponse) {
    setQrCodeData(data)
    setShowQRModal(true)
  }

  function handleCloseQuickCommSetup() {
    setShowQRModal(false)
    setQrCodeData(null)
  }

  // Role guard. Below every hook on purpose — returning above them made the hook
  // order conditional, so a role change mid-mount threw instead of redirecting.
  if (user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" replace />
  }

  // Tables and Teams share a slot, so carry the selection across a property
  // switch instead of stranding the user on a tab that no longer renders.
  // Printers and QR codes have no counterpart outside a restaurant, so they
  // fall back instead.
  const tab: AdminTab =
    isRestaurant && activeTab === 'teams' ? 'tables'
    : !isRestaurant && activeTab === 'tables' ? 'teams'
    : !isRestaurant && (activeTab === 'printers' || activeTab === 'qr') ? 'properties'
    : activeTab

  return (
    <div className="flex-1">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Admin Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage users, properties, devices, and {isRestaurant ? 'tables' : 'teams'}
        </p>
      </div>

      <AdminTabBar active={tab} onChange={setActiveTab} showTables={isRestaurant} />

      <div className="mt-6">
        {tab === 'properties' && <PropertiesTab />}
        {tab === 'teams' && <TeamsTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'devices' && (
          <DevicesTab onOpenQuickCommSetup={handleOpenQuickCommSetup} />
        )}
        {tab === 'tables' && <TablesTab />}
        {tab === 'qr' && <QrCodesTab />}
        {tab === 'printers' && <PrintersTab />}
      </div>

      {/* QuickComm BLE Setup Modal */}
      {qrCodeData && (
        <QuickCommSetupModal
          open={showQRModal}
          onClose={handleCloseQuickCommSetup}
          deviceData={qrCodeData}
          properties={properties}
          teams={allTeams}
          users={allUsers}
        />
      )}
    </div>
  )
}
